import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { AnimMode, AnimVals } from "@/utils/animUtils";
import { getDateRange } from '@/core/utils';
import { EventPricePills, HeadlineAnim, Image, RhLink } from "@components";
import { useMouse } from '@/providers/MouseContext';

import className from 'classnames/bind';
import styles from './ListingEvents.module.scss';
const cx = className.bind(styles);

type EventsListEventProps = {
  event: UpcomingEvents[number];
  index: number;
  isRevealEnabled: boolean;
  isInView: boolean;
};

export default function EventsListEvent({ event, index, isRevealEnabled, isInView }: EventsListEventProps) {
  const { uri, title, eventData, featuredEventImage } = event;
  const { startDate, endDate } = eventData;
  const mouse = useMouse();
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [imageVisible, setImageVisible] = useState(false);
  const rootEl = useRef<HTMLAnchorElement>(null);
  const imageEl = useRef<HTMLImageElement>(null);

  const date = getDateRange(startDate, endDate);

  useEffect(() => {
    if (!rootEl.current) return;
    const rect = rootEl.current.getBoundingClientRect();
    const isHover =
      mouse.x >= rect.left &&
      mouse.x <= rect.right &&
      mouse.y >= rect.top &&
      mouse.y <= rect.bottom;
    const { width: imageWidth, height: imageHeight } = imageEl.current.getBoundingClientRect();
    setOffset({
      x: rect.left + (imageWidth / 2),
      y: rect.top + (imageHeight / 2),
    });
    setImageVisible(isHover);
  }, [mouse]);

  return (
    <li>
      <RhLink
        className={cx('event', { hovered: imageVisible })}
        href={uri}
        ref={rootEl}
      >

        <div className={cx('wrapper')}>

          <motion.div
            className={cx('line')}
            initial={isRevealEnabled ? { width: '0%' } : {}}
            animate={isInView ? { width: '100%' } : {}}
            transition={{ delay: 0.1 + (index * 0.2), duration: 0.3, ease: "easeOut" }}
          />

          { index === 0 && (
            <motion.div
              className={cx('line')}
              initial={isRevealEnabled ? { width: '0%' } : {}}
              animate={isInView ? { width: '100%' } : {}}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
          )}

          <Image
            className={cx('image-mobile')}
            image={featuredEventImage}
          />

          <div className={cx('copy')}>
            { AnimMode === 'all' ? (
              <HeadlineAnim
                tag="p"
                text={title}
                className={cx('title', 'title-lc-s')}
                isRevealEnabled={isRevealEnabled}
                isInView={isInView}
                animInDelay={0.2 + (index * 0.2)}
              />
            ) : (
              <p className={cx('title', 'title-lc-s')}>{title}</p>
            )}
          </div>
          <div className={cx('content')}>
            <div className={cx('item')}>
              <motion.p
                className={cx('pill')}
                initial={isRevealEnabled ? { opacity: 0, y: 30 } : {}}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.25 + (index * 0.2), duration: 0.5, ease: "easeOut" }}
              >{date}</motion.p>
            </div>
            <div className={cx('item')}>
              <motion.div
                initial={isRevealEnabled ? { opacity: 0, y: 30 } : {}}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.25 + (index * 0.2), duration: 0.5, ease: "easeOut" }}
              >
                <EventPricePills
                  pricingType={eventData?.pricingType}
                  price={eventData?.price}
                />
              </motion.div>
            </div>
          </div>

        </div>

        <Image
          ref={imageEl}
          className={cx('image')}
          image={featuredEventImage}
          style={{
            top: `${mouse.y - offset.y}px`,
          }}
        />

      </RhLink>
    </li>
  );
}